import { useEffect, useRef, useState } from 'react';
import { css } from 'styled-system/css';
import { Container } from '../Container';
import { FeatureMockup } from '../featureMockups';
import { FeatureSlide } from './FeatureSlide';
import type { Feature } from './types';

const features: Feature[] = [
  {
    id: 'native',
    title: 'Or import your sheets in Exis',
    body: 'A fully-capable spreadsheet app with a familiar interface. Fast, reliable, and supports all standard formulas. Charts too.',
  },
  {
    id: 'chat',
    title: 'Chat with agents in your sheets',
    body: 'Save time doing manual work. Exis has AI agents built-in, they can work for you, fixing errors, filling patterns, and more.',
  },
  {
    id: 'review',
    title: 'Review, approve, merge',
    body: 'Inspired by pull requests used by professional software engineers, every change in Exis is a proposal that lands only when approved.',
  },
  {
    id: 'routines',
    title: 'Schedule the tedious work',
    body: 'Routines update your sheets on a schedule. Put the daily cell updates on auto-pilot, and see your spreadsheets become self-sustaining.',
  },
];

export function FeaturesSection() {
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function syncActive() {
      const el = listRef.current;
      if (!el) return;
      const items = el.querySelectorAll<HTMLElement>('[data-feature-index]');
      const pin = window.innerHeight * 0.42;
      let best = 0;
      for (const item of items) {
        const rect = item.getBoundingClientRect();
        if (rect.top <= pin && rect.bottom > pin) {
          best = Number(item.getAttribute('data-feature-index')) || 0;
          break;
        }
        if (rect.top > pin) break;
        best = Number(item.getAttribute('data-feature-index')) || 0;
      }
      setActive((prev) => (prev === best ? prev : best));
    }

    syncActive();
    window.addEventListener('scroll', syncActive, { passive: true });
    window.addEventListener('resize', syncActive);
    return () => {
      window.removeEventListener('scroll', syncActive);
      window.removeEventListener('resize', syncActive);
    };
  }, []);

  const activeFeature = features[active] ?? features[0];

  return (
    <section
      id="product"
      aria-label="Features"
      className={css({
        position: 'relative',
        paddingBlock: { base: '16', md: '0' },
        scrollMarginTop: '20',
      })}
    >
      <Container>
        <div
          className={css({
            display: 'grid',
            gridTemplateColumns: {
              base: '1fr',
              md: 'minmax(0, 1fr) minmax(0, 2fr)',
            },
            gap: { base: '10', md: '12', lg: '16' },
          })}
        >
          <div ref={listRef}>
            {features.map((feature, i) => (
              <FeatureSlide
                key={feature.id}
                feature={feature}
                index={i}
                active={active === i}
              />
            ))}
          </div>

          <div
            className={css({
              display: { base: 'none', md: 'block' },
              position: 'sticky',
              top: '48',
              height: '480px',
              alignSelf: 'start',
            })}
          >
            <div
              className={css({
                position: 'relative',
                height: '100%',
              })}
            >
              <div
                aria-hidden="true"
                className={css({
                  position: 'absolute',
                  inset: '-8% -6%',
                  background:
                    'radial-gradient(ellipse 70% 60% at 50% 40%, rgba(139,92,246,0.16), rgba(70,174,191,0.08) 45%, transparent 70%)',
                  pointerEvents: 'none',
                })}
              />
              <div
                aria-hidden="true"
                className={css({
                  position: 'relative',
                  height: '100%',
                })}
              >
                {features.map((feature, i) => (
                  <div
                    key={feature.id}
                    data-active={active === i || undefined}
                    className={css({
                      position: 'absolute',
                      inset: 0,
                      opacity: 0,
                      zIndex: 0,
                      pointerEvents: 'none',
                      '&[data-active]': { opacity: 1, zIndex: 1 },
                    })}
                  >
                    <FeatureMockup feature={feature.id} active={active === i} />
                  </div>
                ))}
              </div>
              <p
                className={css({
                  srOnly: true,
                })}
              >
                Preview: {activeFeature.title}
              </p>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}
